import { useRef } from 'react';
import CheckBox from '@components/DesignLibrary/Atoms/CheckBox';
import { m } from 'framer-motion';
import { createPortal } from 'react-dom';

import usePopover from '@/hooks/usePopover';

import s from '../Filters.module.scss';

type Props<K> = {
  onChange: (selected: Array<K>) => unknown;
  label: string;
  items: Array<{
    label: string;
    isChecked: boolean;
    key: K;
  }>;
};

const FilterMany = <K extends string>({ onChange, label, items }: Props<K>): React.ReactElement => {
  const { isOpen, setIsOpen, menuRef, triggerRef } = usePopover('bottom-left');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const containerRef = useRef<HTMLDivElement>(null);

  const checked = items.filter(item => item.isChecked);
  const checkedKeys = checked.map(item => item.key);

  const handleChange = (key: string, newValue: boolean): void => {
    const newChecked = items.filter(item => (item.key === key ? newValue : item.isChecked)).map(item => item.key);

    onChangeRef.current(newChecked);
  };

  return (
    <div className={s.container} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={isOpen ? { backgroundColor: 'var(--color-gray-25)' } : undefined}
      >
        <span>
          {label}
          {checked.length > 0 && `: ${checked.map(item => item.label).join(', ')}`}
        </span>
      </button>
      {isOpen && createPortal(
        <m.fieldset
          ref={menuRef as unknown as React.Ref<HTMLFieldSetElement>}
          className={s.inputs}
          initial={{
            opacity: 0,
            y: 8,
          }}
          animate={{
            opacity: 1,
            y: 4,
          }}
        >
          {items.map(i => (
            <CheckBox
              key={i.key}
              size="sm"
              style="round"
              isChecked={checkedKeys.includes(i.key)}
              onChange={value => handleChange(i.key, value)}
              label={i.label}
            />
          ))}
        </m.fieldset>,
        document.body,
      )}
    </div>
  );
};

export default FilterMany;
